/**
 * ConfigUploadPanel.tsx
 * Matches OattFileUpload layout (image 2):
 *   Bold heading → single "Select Files" button → file list below →
 *   Upload All (left) | Refresh (far right) → progress bar
 *
 * Uses useConfigUpload hook which calls POST /api/config-upload.
 * Auto-classifies vendor vs meter file by reading Excel headers (SheetJS).
 */

import { useState } from 'react';
import { Upload, Progress, Alert, Card, Button } from 'antd';
import { UploadOutlined, ReloadOutlined, CloseOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { NSPButton } from '@components/ui/NSPButton';
import { useConfigUpload } from '@hooks/useConfigUpload';
import styles from './configuploadpanel.module.css';

interface ConfigUploadPanelProps {
  onUploadComplete?: () => void;
}

// ── Required column headers per file type ────────────────────────────

const VENDOR_REQUIRED = ['Name', 'Address 1', 'PO#', 'Invoice Prefix'];
const METER_REQUIRED = [
  'Vendor Name', 'Meter ID', 'Meter #', 'Meter Type',
  'Energy Type', 'Region', 'STAT GL', 'Dollar GL', 'Contract Rate',
];

// ── Client-side header validation (SheetJS, header row only) ─────────

async function parseXlsxHeaders(buffer: ArrayBuffer): Promise<string[]> {
  const { read, utils } = await import('xlsx');
  try {
    const data = new Uint8Array(buffer);
    const wb = read(data, { type: 'array', sheetRows: 2 });
    const ws = wb.Sheets[wb.SheetNames[0]];
    const rows = utils.sheet_to_json<unknown[]>(ws, { header: 1 }) as unknown[][];
    return (rows[0] ?? []).map((h) => String(h ?? '').trim());
  } catch {
    return [];
  }
}

async function getExcelHeaders(file: File): Promise<string[]> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      parseXlsxHeaders(e.target?.result as ArrayBuffer).then(resolve);
    };
    reader.onerror = () => resolve([]);
    reader.readAsArrayBuffer(file);
  });
}

function missingCols(headers: string[], required: string[]): string[] {
  return required.filter(
    (r) => !headers.some((h) => h.toLowerCase() === r.toLowerCase())
  );
}

/**
 * Auto-detects which file is Vendor and which is Meter by matching headers.
 * Returns an error string if classification or validation fails.
 */
async function classifyFiles(files: File[]): Promise<{
  vendorFile: File | null;
  meterFile: File | null;
  error: string | null;
}> {
  if (files.length !== 2) {
    return {
      vendorFile: null, meterFile: null,
      error: 'Please select exactly 2 files: one Vendor file and one Meter file.',
    };
  }

  const [a, b] = files;
  const [headersA, headersB] = await Promise.all([
    getExcelHeaders(a),
    getExcelHeaders(b),
  ]);

  const aMissingVendor = missingCols(headersA, VENDOR_REQUIRED);
  const bMissingVendor = missingCols(headersB, VENDOR_REQUIRED);

  let vendorFile: File;
  let meterFile: File;
  let meterHeaders: string[];

  if (aMissingVendor.length === 0) {
    vendorFile = a; meterFile = b; meterHeaders = headersB;
  } else if (bMissingVendor.length === 0) {
    vendorFile = b; meterFile = a; meterHeaders = headersA;
  } else {
    return {
      vendorFile: null, meterFile: null,
      error: `Neither file matches the Vendor format. "${a.name}" is missing: ${aMissingVendor.join(', ')}.`,
    };
  }

  const meterMissing = missingCols(meterHeaders, METER_REQUIRED);
  if (meterMissing.length > 0) {
    return {
      vendorFile: null, meterFile: null,
      error: `Meter file "${meterFile.name}" is missing required column(s): ${meterMissing.join(', ')}.`,
    };
  }

  return { vendorFile, meterFile, error: null };
}

// ── Component ─────────────────────────────────────────────────────────

const getProgressStatus = (status: string): 'active' | 'success' | 'exception' | 'normal' => {
  if (status === 'active') return 'active';
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'exception';
  return 'normal';
};

export default function ConfigUploadPanel({ onUploadComplete }: ConfigUploadPanelProps) {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [validating, setValidating] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [progressStatus, setProgressStatus] = useState<'idle' | 'active' | 'completed' | 'failed'>('idle');
  const [progressPercent, setProgressPercent] = useState(0);
  const { uploading, result, validationError, uploadFiles, reset } = useConfigUpload();

  const clearErrors = () => {
    setClientError(null);
    if (result || validationError) reset();
  };

  // ── File list handler ───────────────────────────────────────────

  const handleChange = ({ fileList: newList }: { fileList: UploadFile[] }) => {
    setFileList(newList.slice(-2)); // keep max 2
    clearErrors();
  };

  // ── Upload with pre-validation ──────────────────────────────────

  const handleCloseProgress = () => {
    setProgressStatus('idle');
    setProgressPercent(0);
    setProgressMessage(null);
  };

  const handleUploadAll = async () => {
    const files = fileList.map((f) => f.originFileObj as File).filter(Boolean);

    if (files.length < 2) {
      setClientError('Please select both files.');
      return;
    }

    setProgressMessage('Processing started...');
    setProgressStatus('active');
    setProgressPercent(30);
    setValidating(true);
    setClientError(null);

    const { vendorFile, meterFile, error } = await classifyFiles(files);
    setValidating(false);

    if (error || !vendorFile || !meterFile) {
      setClientError(error ?? 'Invalid files');
      setProgressStatus('idle');
      setProgressPercent(0);
      setProgressMessage(null);
      return;
    }

    setProgressPercent(70);

    console.debug('[ConfigUploadPanel] Calling uploadFiles with:', {
      vendor: { name: vendorFile.name, size: vendorFile.size },
      meter: { name: meterFile.name, size: meterFile.size },
    });

    try {
      const ok = await uploadFiles(vendorFile, meterFile);

      if (ok) {
        setProgressMessage('Completed');
        setProgressStatus('completed');
        setProgressPercent(100);
        setFileList([]);
        onUploadComplete?.();
      } else {
        setProgressMessage('Processing failed');
        setProgressStatus('failed');
        setProgressPercent(100);
      }
    } catch {
      setProgressMessage('Processing failed');
      setProgressStatus('failed');
      setProgressPercent(100);
    }
  };

  // ── Derived state ───────────────────────────────────────────────

  const isBusy = validating || uploading;
  const isSuccess = result?.success === true;
  const showProgress = progressStatus !== 'idle';
  const canClose = progressStatus === 'completed' || progressStatus === 'failed';
  const errorMsg =
    clientError ??
    validationError ??
    (result && !result.success ? (result.errors?.[0] ?? 'Upload failed.') : null);

  return (
    <div className={styles.container} data-testid="config-upload">

      {/* ── Heading ─────────────────────────────────────────────── */}
      <div className={styles.headerTitle}>Upload Config Files</div>

      {/* ── Error alert ─────────────────────────────────────────── */}
      {errorMsg && (
        <Alert
          type="error"
          showIcon
          closable
          onClose={clearErrors}
          className={styles.alert}
          message="Upload Failed"
          description={errorMsg}
          data-testid="config-upload-error-alert"
        />
      )}

      {/* ── Success alert ────────────────────────────────────────── */}
      {isSuccess && result && (
        <Alert
          type="success"
          showIcon
          closable
          onClose={reset}
          className={styles.alert}
          message="Upload Successful"
          data-testid="config-upload-success-alert"
          description={
            <span>
              <strong>{result.summary.vendorsInserted}</strong> vendor(s),{' '}
              <strong>{result.summary.glInserted}</strong> GL entries(s),{' '}
              <strong>{result.summary.metersInserted}</strong> meter(s),{' '}
              <strong>{result.summary.rateContractsInserted}</strong> rate contract(s) inserted.
              {result.errors.length > 0 && (
                <details style={{ marginTop: 6, cursor: 'pointer' }}>
                  <summary data-testid="config-upload-warnings" data-count={String(result.errors.length)}>{result.errors.length} warning(s)</summary>
                  <ul className={styles.warnList}>
                    {result.errors.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </details>
              )}
            </span>
          }
        />
      )}

      {/* ── Single "Select Files" button ─────────────────────────── */}
      <Upload
        multiple
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        fileList={fileList}
        beforeUpload={() => false}
        onChange={handleChange}
        data-testid="file-input"
        onRemove={(file) => {
          setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
          clearErrors();
        }}
        showUploadList={{ showRemoveIcon: true, showPreviewIcon: false }}
      >
        <NSPButton icon={<UploadOutlined />} disabled={isBusy} data-testid="select-files-button">
          Select Files
        </NSPButton>
      </Upload>

      {/* ── Progress bar (below Select Files, full width) ────────── */}
      {showProgress && (
        <Card
          size="small"
          className={styles.progressbar}
          data-testid="upload-progress"
          data-status={progressStatus}
          extra={
            canClose && (
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={handleCloseProgress}
                data-testid="progress-close-button"
              />
            )
          }
        >
          <Progress
            percent={progressPercent}
            status={getProgressStatus(progressStatus)}
            data-testid="upload-progress-bar"
            data-percent={String(progressPercent)}
            strokeColor={{
              '0%': '#108ee9',
              '100%': '#87d068',
            }}
          />
          <div className={styles.uploadProgress} data-testid="progress-message">
            {progressMessage}
          </div>
        </Card>
      )}

      {/* ── Action bar: Upload All (left) | Refresh (far right) ─── */}
      <div className={styles.actionBar}>
        <NSPButton
          onClick={handleUploadAll}
          disabled={fileList.length === 0 || isBusy}
          loading={isBusy}
          data-testid="upload-all-button"
        >
          {validating ? 'Validating...' : uploading ? 'Uploading...' : 'Upload All'}
        </NSPButton>

        <NSPButton
          icon={<ReloadOutlined />}
          onClick={() => globalThis.location.reload()}
          data-testid="refresh-button"
        >
          Refresh
        </NSPButton>
      </div>

    </div>
  );
}